import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import { take } from 'rxjs/operators';
import moment from 'moment';

import { CriticalOrderStagesGQL, DashboardType, UpdateCriticalStageGQL } from '../../../../generated/graphql';
import { StandupDashboardServiceService } from '../standup-dashboard-service.service';
import { RelayIdService } from '../../../shared/relay-id.service';
import { FilterInterface } from '../../../shared/filter-bar/filter-bar.component';
import { MatDialog } from '@angular/material/dialog';
import {
  CriticalOrdersCommentDialogComponent
} from './critical-orders-comment-dialog/critical-orders-comment-dialog.component';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-critical-orders-table',
  templateUrl: './critical-orders-table.component.html',
  styleUrls: ['./critical-orders-table.component.scss']
})
export class CriticalOrdersTableComponent implements OnInit {
  isLoading = true;
  dataSource;
  displayedColumns = ['isImportant', 'region', 'order', 'company', 'stage', 'comment'];
  initDates: FilterInterface;
  stages = [];
  selectedStageControlsSet: {[key: string]: FormControl} = {};
  selectedStageControlsSetKeys = Object.keys(this.selectedStageControlsSet);

  @Input() dashboardId: string;
  @Input() cardId: string;
  @Input() dashboardTitle: string;
  @Input() cardDescription: string;
  @Input() card;
  @Input() dashboardItem: DashboardType;

  constructor(
    private dashboardService: StandupDashboardServiceService,
    private criticalOrderStagesGQL: CriticalOrderStagesGQL,
    private updateCriticalStageGQL: UpdateCriticalStageGQL,
    private relayIdService: RelayIdService,
    private dialog: MatDialog,
  ) { }

  openDescription(template: TemplateRef<any>) {
    this.dialog.open(template, { autoFocus: false, maxHeight: '80dvh' });
  }

  formatInnerText(text: string): string {
    return text.replace(/\n/g, '<br>');
  }

  ngOnInit(): void {
    this.criticalOrderStagesGQL.fetch().subscribe(v => {
      this.stages = v.data.criticalOrderStages.map(val => ({
        id: val.id,
        title: val.title
      }));
    });

    this.dashboardService.changedCommentsSubject.subscribe(data => {
      if (data) {
        this.dataSource = this.dataSource.map(item => {
          if (data.id === item.metricId) {
            return {
              ...item,
              commentsSet: data.comments,
              commentText: data.comments.map(val => val.comment)
            };
          } else {
            return item;
          }
        });
      }
    });

    // Default to this week's data
    this.initDates = {
      dateRange: {
        dateFrom: moment().startOf('isoWeek'),
        dateTo: moment().endOf('isoWeek')
      }
    };

    const cardId = this.cardId ?? this.card?.id;
    if (cardId) {
      this.loadCriticalOrders(
        moment().startOf('isoWeek').format('YYYY-MM-DD'),
        moment().endOf('isoWeek').format('YYYY-MM-DD')
      );
    }
  }

  onFilterChanges(filters: FilterInterface): void {
    if (filters.dateRange?.dateFrom && filters.dateRange?.dateTo) {
      const dateFrom = moment(filters.dateRange.dateFrom).format('YYYY-MM-DD');
      const dateTo = moment(filters.dateRange.dateTo).format('YYYY-MM-DD');
      this.loadCriticalOrders(dateFrom, dateTo);
    }
  }

  updateCriticalStage(input) {
    this.updateCriticalStageGQL.mutate({ input: { ...input } }).subscribe(v => {});
  }

  private loadCriticalOrders(dateFrom: string, dateTo: string): void {
    const cardId = this.cardId ?? this.card?.id;
    if (!cardId) {
      return;
    }

    this.isLoading = true;

    const dashboardId = this.dashboardId ?? this.dashboardItem?.id;
    this.dashboardService.getMetricTableValues(cardId, dashboardId, dateFrom, dateTo).pipe(take(1)).subscribe((metricGroups) => {
      const result = [];
      metricGroups.forEach((item: any) => {
        item.metricGroup.metricvalueSet?.edges.forEach(v => {
          const orderId = this.relayIdService.parseId(v?.node?.order?.id);
          const orderTitle = v?.node?.order?.specification?.positionTitle;
          if (!this.selectedStageControlsSetKeys.includes(v?.node?.id)) {
            this.selectedStageControlsSet[v?.node?.id] = new FormControl(v?.node?.criticalOrderStage?.id);
          }
          result.push({
            orderId: v?.node?.order?.id,
            metricId: v?.node?.id,
            isImportant: v?.node?.isImportant,
            company: v?.node?.client?.name,
            order: orderTitle ? `${orderTitle} (${orderId})` : orderId,
            commentsSet: v?.node?.metricvaluecommentSet?.edges?.map(val => ({
              comment: val?.node?.comment,
              id: val?.node?.id
            })),
            commentText: v?.node?.metricvaluecommentSet?.edges?.map(val => val.node.comment),
            region: v?.node?.regionGroup?.title
          });
        });
      });
      this.dataSource = result;
      this.isLoading = false;
    }, e => this.isLoading = false);
  }

  changeImportance(element) {
    this.dashboardService.updateMetricValueImportance(element.metricId, !element.isImportant).subscribe(
      re => {
        element.isImportant = re.data.updateMetricValue.metricValue.isImportant;
      }
    );
  }

  openComment(element) {
    this.dialog.open(CriticalOrdersCommentDialogComponent, {
      data: {
        comments: [...element?.commentsSet.map(c => ({ ...c }))],
        metricId: element?.metricId
      }
    });
  }
}
